import { useEffect, useMemo, useState } from 'react';
import type { Course, Hole, LngLat } from '../lib/types';
import type { Fix } from '../lib/gps';
import { bearingDeg, distanceM, formatDistance, playingDistance } from '../lib/geo';
import { formatKm, loadDirectory, nearestCourses, type SearchHit } from '../lib/directory';

interface Props {
  course: Course;
  hole: Hole;
  teeColour: string;
  unit: 'm' | 'yd';
  fix: Fix | null;
  onOpen: () => void;
  brand: string;
}

/**
 * Dashboard widget — the glanceable tile that sits on the head unit's home
 * screen next to (or instead of) the map and music tiles.
 *
 * Rules that make a widget a widget:
 *   - ONE number big enough to read at a glance from the driver's seat
 *   - no scrolling, no input beyond a single tap that opens the full app
 *   - it must say something useful in every state, including "not on a course"
 *   - it re-lays out for whatever box the launcher gives it
 *
 * Rendered at /widget so an Android AppWidget or launcher tile can host it in a
 * WebView without loading the whole app shell.
 */
export default function WidgetScreen({ course, hole, teeColour, unit, fix, onOpen, brand }: Props) {
  const [nearby, setNearby] = useState<SearchHit[]>([]);
  const [clock, setClock] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setClock(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // How far is the buggy from the course it thinks it is on? If it is a long
  // way, the golfer is not playing — show them what is nearby instead.
  const awayFromCourse = fix ? distanceM(fix.pos, course.centre) > 8000 : false;

  useEffect(() => {
    if (!fix || !awayFromCourse) return;
    loadDirectory()
      .then((d) => setNearby(nearestCourses(d, fix.pos, 3)))
      .catch(() => setNearby([]));
  }, [fix, awayFromCourse]);

  const yardage = useMemo(() => {
    const from: LngLat = fix?.pos ?? hole.tees[teeColour] ?? hole.tees.White;
    const middle = distanceM(from, hole.green.middle);
    const front = distanceM(from, hole.green.front);
    const back = distanceM(from, hole.green.back);
    const rise =
      hole.greenElevation != null && hole.teeElevation != null
        ? hole.greenElevation - hole.teeElevation
        : 0;
    const play = playingDistance({ flatM: middle, riseM: rise, shotBearing: bearingDeg(from, hole.green.middle) });
    return { middle, front, back, play: play.metres };
  }, [fix, hole, teeColour]);

  const time = clock.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });

  // --- off-course state ---------------------------------------------------
  if (awayFromCourse || !fix) {
    return (
      <button className="widget" onClick={onOpen} aria-label={`Open ${brand}`}>
        <div className="widget-head">
          <span className="widget-brand">{brand}</span>
          <span className="widget-time">{time}</span>
        </div>
        {nearby.length ? (
          <>
            <div className="widget-label">NEAREST COURSE</div>
            <div className="widget-course">{nearby[0].name}</div>
            <div className="widget-sub">
              {formatKm(nearby[0].distanceM)} away
              {nearby[0].holes ? ` · ${nearby[0].holes} holes` : ''}
            </div>
            <div className="widget-foot">
              {nearby.slice(1).map((c) => (
                <span key={c.id}>
                  {c.name} {formatKm(c.distanceM)}
                </span>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="widget-label">{fix ? 'NOT ON A COURSE' : 'WAITING FOR GPS'}</div>
            <div className="widget-course">{fix ? 'Tap to find a course' : 'Acquiring signal'}</div>
            <div className="widget-sub">
              {fix ? '1,000+ Australian courses' : 'Park with a clear view of the sky'}
            </div>
          </>
        )}
      </button>
    );
  }

  // --- on-course state ----------------------------------------------------
  return (
    <button className="widget" onClick={onOpen} aria-label={`Open ${brand}`}>
      <div className="widget-head">
        <span className="widget-brand">{brand}</span>
        <span className="widget-time">
          {fix.stale ? 'GPS LOST' : `±${Math.round(fix.accuracy)}m`} · {time}
        </span>
      </div>

      <div className="widget-hero">
        <span className="widget-big">{formatDistance(yardage.play, unit)}</span>
        <span className="widget-unit">{unit}</span>
      </div>
      <div className="widget-label">TO CENTRE · HOLE {hole.number} · PAR {hole.par}</div>

      <div className="widget-fmb">
        <span>
          F <strong>{formatDistance(yardage.front, unit)}</strong>
        </span>
        <span>
          M <strong>{formatDistance(yardage.middle, unit)}</strong>
        </span>
        <span>
          B <strong>{formatDistance(yardage.back, unit)}</strong>
        </span>
      </div>
      <div className="widget-foot">
        <span>{course.name}</span>
      </div>
    </button>
  );
}
